'use client';

import { Icon } from '@/components/ui/Icon';
import type { Weekday } from '@/lib/const';
import { isDateDisabled } from '@/lib/stores/appointmentScheduling';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import type { CalendarProps } from 'react-calendar';

import 'react-calendar/dist/Calendar.css';
import styles from './CalendarBase.module.scss';

export interface CalendarBaseProps {
  value: string;
  minDate: Date;
  maxDate: Date;
  nonWorkingDays?: string[];
  unavailableAppointmentWeekdays?: Weekday[];
  onChange: (date: Date) => void;
  onActiveStartDateChange?: CalendarProps['onActiveStartDateChange'];
  disabled?: boolean;
  /** Page layout uses accent orange for the selected date. */
  layout?: 'modal' | 'page';
}

/**
 * Date picker for appointment booking with disabled days handling.
 */
export function CalendarBase({
  value,
  minDate,
  maxDate,
  nonWorkingDays = [],
  unavailableAppointmentWeekdays = [],
  onChange,
  onActiveStartDateChange,
  disabled = false,
  layout = 'modal',
}: Readonly<CalendarBaseProps>) {
  const calendarWrapperRef = useRef<HTMLDivElement>(null);

  const parsedValue = value
    ? new Date(
        Number.parseInt(value.split('-')[0], 10),
        Number.parseInt(value.split('-')[1], 10) - 1,
        Number.parseInt(value.split('-')[2], 10)
      )
    : undefined;

  useEffect(() => {
    const calendarEl = calendarWrapperRef.current;
    if (!calendarEl) return;

    const tiles = calendarEl.querySelectorAll<HTMLButtonElement>('.react-calendar__tile');
    tiles.forEach((tile) => {
      if (tile.disabled) {
        tile.setAttribute('tabindex', '-1');
        return;
      }
      tile.setAttribute('tabindex', '0');
      tile.setAttribute(
        'aria-pressed',
        tile.classList.contains('react-calendar__tile--active') ? 'true' : 'false'
      );
    });
  }, [value, nonWorkingDays, unavailableAppointmentWeekdays]);

  return (
    <div
      ref={calendarWrapperRef}
      className={clsx(styles.wrapper, disabled && styles.wrapperDisabled)}
    >
      <Calendar
        className={clsx(
          'react-calendar',
          styles.calendar,
          layout === 'page' && styles.calendarPage
        )}
        calendarType={layout === 'page' ? 'iso8601' : undefined}
        value={parsedValue}
        minDate={minDate}
        maxDate={maxDate}
        showNeighboringMonth={false}
        formatMonthYear={(_, date) => dayjs(date).format('MMMM YYYY')}
        formatShortWeekday={(_locale, date) => ['S', 'M', 'T', 'W', 'T', 'F', 'S'][date.getDay()]}
        prevLabel={<Icon name="chevron-left" size={16} aria-label="Previous month" />}
        nextLabel={<Icon name="chevron-right" size={16} aria-label="Next month" />}
        onChange={(nextValue) => {
          if (disabled) return;
          if (nextValue instanceof Date) {
            onChange(nextValue);
          }
        }}
        onActiveStartDateChange={onActiveStartDateChange}
        tileDisabled={({ date }) =>
          disabled || isDateDisabled(date, unavailableAppointmentWeekdays, nonWorkingDays)
        }
      />
    </div>
  );
}

CalendarBase.displayName = 'CalendarBase';
